import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Box, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import {
  WASTE_CONTAINER_TYPE_BY_CODE,
  isWasteContainerTypeCode,
} from "encamp-shared/src/constants/wasteContainerTypes";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  Issue,
  IssueLevel,
  RcraInfoFederalWasteGeneratorStatus,
  WasteAccumulationAreaType,
  WasteContainerCreateInput,
  WasteContainerUpdateInput,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, UseFormReturn, UseFormSetValue } from "react-hook-form";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  AccumulationAreaDialog,
  CreatedAccumulationArea,
} from "../AccumulationAreas/AccumulationAreaDialog";
import {
  CONTAINER_MUTATION_REFETCH_QUERIES,
  CREATE_WASTE_CONTAINER_MUTATION,
  FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY,
  UPDATE_WASTE_CONTAINER_MUTATION,
  VALIDATE_WASTE_CONTAINER_INPUT,
  WASTE_CONTAINER_FACILITY_CONTEXT_QUERY,
} from "./api";
import { toGraphqlUnitType } from "./containerDisplay";
import {
  AreaOption,
  ContainerFormFields,
  WasteContainerDetail,
  WasteContainerFormValues,
  WasteStreamOption,
} from "./ContainerFormFields";
import { ContainerFormPreview } from "./ContainerFormPreview";
import { MoveContainerDialog } from "./MoveContainerDialog";

function toDateTimeInput(isoDate: string | null): string | undefined {
  if (!isoDate) {
    return undefined;
  }
  return toUtcMidnightIsoFromDateLike(DateTime.fromISO(isoDate));
}

/**
 * Fields whose form→input mapping is byte-for-byte the same for create and
 * update. Both input mappers spread this so a new shared column is wired in
 * one place instead of drifting between the two. containerLabel is not here:
 * its blank handling diverges by mode (create auto-generates a label, edit
 * rejects a blank), like the nullable fields in toContainerUpdateInput.
 */
function toSharedContainerFields(values: WasteContainerFormValues) {
  return {
    containerType: values.containerType,
    isPermanent: values.isPermanent,
    isFlowThrough: values.isFlowThrough,
    isEstimate: values.isEstimate,
    sqgOver200Miles: values.sqgOver200Miles,
  };
}

/**
 * The validator takes the create-input shape for both modes; edit maps its
 * identity fields onto it, plus the container's CURRENT area so placement
 * rules (tanks are central-only) block inline instead of surfacing as a raw
 * mutation error after Save.
 */
function toContainerInput(
  values: WasteContainerFormValues,
  mode: "create" | "edit",
  currentAccumulationAreaId?: string | null
): WasteContainerCreateInput {
  return {
    ...toSharedContainerFields(values),
    // Create treats a blank label as "auto-generate"; edit sends the trimmed
    // value verbatim so a cleared field reaches the validator/mutation as ""
    // and is rejected rather than silently reverting to the stored label.
    containerLabel:
      mode === "create"
        ? (values.containerLabel ?? "").trim() || undefined
        : (values.containerLabel ?? "").trim(),
    facilityId: values.facilityId,
    capacity: safeParseFloat(values.capacity) ?? undefined,
    capacityUnit: values.capacityUnit ?? undefined,
    wasteStreamId: values.wasteStreamId ?? undefined,
    estimatedPickupDate: toDateTimeInput(values.estimatedPickupDate),
    ...(mode === "create"
      ? {
          accumulationAreaId: values.accumulationAreaId ?? undefined,
          initialAmount: safeParseFloat(values.initialAmount) ?? undefined,
          initialAmountUnit: values.initialAmountUnit ?? undefined,
          accumulationStartDate: toDateTimeInput(values.accumulationStartDate),
          movedToCentralDate: toDateTimeInput(values.movedToCentralDate),
        }
      : { accumulationAreaId: currentAccumulationAreaId ?? undefined }),
  };
}

/**
 * Update mutation input. Shares the common scalars with create via
 * toSharedContainerFields; the nullable fields diverge deliberately — here a
 * null is an instruction to clear (detach the stream, drop the capacity/unit),
 * whereas create coalesces the same fields to undefined ("leave unset").
 */
function toContainerUpdateInput(
  values: WasteContainerFormValues,
  containerId: string,
  estimatedPickupDateDirty: boolean
): WasteContainerUpdateInput {
  return {
    ...toSharedContainerFields(values),
    // A cleared label arrives as "" so the mutation's blank guard rejects it
    // instead of Prisma leaving the stored label unchanged.
    containerLabel: (values.containerLabel ?? "").trim(),
    id: containerId,
    capacity: safeParseFloat(values.capacity),
    capacityUnit: values.capacityUnit,
    wasteStreamId: values.wasteStreamId,
    // Only send the date when the user actually touched it: re-normalizing the
    // stored instant through the browser's local zone can shift it to a
    // different calendar day for negative-UTC-offset users.
    estimatedPickupDate: estimatedPickupDateDirty
      ? (toDateTimeInput(values.estimatedPickupDate) ?? null)
      : undefined,
  };
}

function missingFacilityIssue(): Issue {
  return {
    level: IssueLevel.Critical,
    key: "facilityId",
    message: "Select a facility for this container",
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

function useWasteContainerInputValidation(
  mode: "create" | "edit",
  currentAccumulationAreaId?: string | null,
  currentContainerId?: string | null
) {
  const [query] = useLazyQuery(VALIDATE_WASTE_CONTAINER_INPUT, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (values: WasteContainerFormValues): Promise<Issue[]> => {
      if (!values.facilityId) {
        return [missingFacilityIssue()];
      }
      const { data } = await query({
        variables: {
          input: toContainerInput(values, mode, currentAccumulationAreaId),
          // Editing: the container's own label must not read as a collision.
          currentContainerId: currentContainerId ?? undefined,
        },
      });
      return data?.wasteContainerInputValidator ?? [];
    },
    [query, mode, currentAccumulationAreaId, currentContainerId]
  );
}

/**
 * Who the container belongs to and the status its deadline derives from —
 * shown by the form, never asked for.
 */
function useFacilityContainerContext(facilityId: string) {
  const { data } = useQuery(WASTE_CONTAINER_FACILITY_CONTEXT_QUERY, {
    variables: { facilityId },
    skip: !facilityId,
  });
  const facilityContext = data?.facility;

  // The primary, active EPA id is the one the container's label and manifests
  // will carry; a facility can hold several.
  const primaryEpaId =
    facilityContext?.epaIdStatuses.find(
      (status) => status.isPrimaryForFacility && !status.isInactive
    )?.epaId ?? facilityContext?.epaIdStatuses.find((status) => !status.isInactive)?.epaId;

  return {
    facilityContext,
    primaryEpaId,
    generatorStatus: facilityContext?.accumulationSummary?.generatorStatus,
  };
}

/**
 * The areas a new container may be placed in.
 *
 * A tank must sit in a central area, so the list narrows with the permanent
 * checkbox. An inline-created area is selected before the options query
 * refetches, so it is included directly — otherwise MUI logs a "value not in
 * options" warning and the field appears to clear itself.
 */
function useVisibleAreaOptions({
  mode,
  facilityId,
  isPermanent,
  selectedArea,
}: {
  mode: "create" | "edit";
  facilityId: string;
  isPermanent: boolean;
  selectedArea: AreaOption | null;
}): AreaOption[] {
  const { data } = useQuery(FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY, {
    variables: { facilityId },
    skip: mode !== "create" || !facilityId,
  });

  const areaOptions: AreaOption[] = data?.facility?.accumulationAreas?.items ?? [];
  const filtered = isPermanent
    ? areaOptions.filter((area) => area.type === WasteAccumulationAreaType.Central)
    : areaOptions;

  if (selectedArea && !filtered.some((area) => area.id === selectedArea.id)) {
    return [...filtered, selectedArea];
  }
  return filtered;
}

/**
 * Saving the form, in whichever direction the dialog is open for.
 *
 * Warn-and-allow: CRITICAL issues block the save; ERROR issues have already
 * been surfaced on the fields and in the issue list, but the user may still
 * save through them.
 */
function useSaveWasteContainer({
  mode,
  container,
  onClose,
  onCreated,
  validateInput,
  getValues,
  dirtyFields,
  setSubmitAttempted,
}: {
  mode: "create" | "edit";
  container?: WasteContainerDetail;
  onClose: () => void;
  onCreated?: (containerId: string) => void;
  validateInput: (values: WasteContainerFormValues) => Promise<Issue[]>;
  getValues: () => WasteContainerFormValues;
  dirtyFields: { estimatedPickupDate?: unknown };
  setSubmitAttempted: (attempted: boolean) => void;
}) {
  const alerts = useAlerts();

  const [createContainer, { loading: isCreating }] = useMutation(CREATE_WASTE_CONTAINER_MUTATION, {
    refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
  });
  const [updateContainer, { loading: isUpdating }] = useMutation(UPDATE_WASTE_CONTAINER_MUTATION, {
    // The summary refetch matters on edit too: reassigning the waste stream
    // moves the container between the acute/non-acute/excluded cap buckets.
    refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
  });

  const onSubmit = async () => {
    setSubmitAttempted(true);
    const values = getValues();
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "create") {
      await createContainer({
        variables: { input: toContainerInput(values, "create") },
        onCompleted: (data) => {
          alerts.success("Container created");
          onCreated?.(data.createWasteContainer.id);
          onClose();
        },
        onError: (error) => {
          alerts.error(error.message ?? "An error occurred while creating the container");
        },
      });
      return;
    }
    if (!container) {
      return;
    }
    await updateContainer({
      variables: {
        input: toContainerUpdateInput(
          values,
          container.id,
          Boolean(dirtyFields.estimatedPickupDate)
        ),
      },
      onCompleted: () => {
        alerts.success("Container updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while updating the container");
      },
    });
  };

  return { onSubmit, isSaving: isCreating || isUpdating };
}

export type WasteContainerDialogProps = {
  onClose: () => void;
  /** Locks the create form to this facility (facility-scoped list, tile). */
  facilityId?: string;
  /** Present in edit mode. */
  container?: WasteContainerDetail;
  onCreated?: (containerId: string) => void;
};

/** What the form opens with, which is the container's own values on edit. */
function useWasteContainerFormDefaults(
  container: WasteContainerDetail | undefined,
  facilityId?: string
) {
  return useMemo<WasteContainerFormValues>(
    () => ({
      facilityId: container?.facilityId ?? facilityId ?? "",
      containerLabel: container?.containerLabel ?? "",
      containerType: container?.containerType ?? "",
      capacity: container?.capacity != null ? String(container.capacity) : "",
      capacityUnit: container?.capacityUnit ?? null,
      isPermanent: container?.isPermanent ?? false,
      isFlowThrough: container?.isFlowThrough ?? false,
      wasteStreamId: container?.wasteStream?.id ?? null,
      accumulationAreaId: null,
      initialAmount: "",
      initialAmountUnit: null,
      isEstimate: container?.isEstimate ?? false,
      accumulationStartDate: null,
      movedToCentralDate: null,
      sqgOver200Miles: container?.sqgOver200Miles ?? false,
      estimatedPickupDate: container?.estimatedPickupDate ?? null,
    }),
    [container, facilityId]
  );
}

/**
 * Selecting a container type prefills capacity from the industry-standard size
 * for it — but never over something the user has already typed.
 */
function usePrefillCapacityForType(
  getValues: (name: "capacity") => string | null,
  setValue: UseFormSetValue<WasteContainerFormValues>
) {
  return useCallback(
    (typeCode: string) => {
      if (!isWasteContainerTypeCode(typeCode)) {
        return;
      }
      const definition = WASTE_CONTAINER_TYPE_BY_CODE[typeCode];
      if (definition.defaultCapacity == null || definition.defaultCapacityUnit == null) {
        return;
      }
      if ((getValues("capacity") ?? "").trim() !== "") {
        return;
      }
      setValue("capacity", String(definition.defaultCapacity), { shouldDirty: true });
      setValue("capacityUnit", toGraphqlUnitType(definition.defaultCapacityUnit), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [getValues, setValue]
  );
}

function ContainerPreviewRail({
  form,
  generatorStatus,
}: {
  form: UseFormReturn<WasteContainerFormValues>;
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
}) {
  const { watch } = form;

  return (
    // Sticky within the dialog's scrolling content (md+ only — on xs the rail
    // stacks below the form): the clock/fill charts react live to fields that
    // sit below the fold, so the rail must stay in view while the form scrolls.
    <Box
      sx={{
        width: { xs: "100%", md: 300 },
        flexShrink: 0,
        position: { md: "sticky" },
        top: { md: 0 },
      }}
    >
      <ContainerFormPreview
        generatorStatus={generatorStatus}
        movedToCentralDate={watch("movedToCentralDate")}
        sqgOver200Miles={watch("sqgOver200Miles")}
        amount={safeParseFloat(watch("initialAmount"))}
        amountUnit={watch("initialAmountUnit")}
        capacity={safeParseFloat(watch("capacity"))}
        capacityUnit={watch("capacityUnit")}
      />
    </Box>
  );
}

/**
 * Everything the dialog needs to decide before it can draw itself.
 *
 * Split from the render because the two answer different questions: this is
 * which container is being edited, what the form currently holds, and what
 * the facility allows; the component below is only where those land.
 */
function useWasteContainerDialogState({
  onClose,
  facilityId,
  container,
  onCreated,
}: WasteContainerDialogProps) {
  const mode: "create" | "edit" = container ? "edit" : "create";

  const [selectedWasteStream, setSelectedWasteStream] = useState<WasteStreamOption | null>(
    container?.wasteStream ?? null
  );
  // Create-only: the picked area (id lives in form state; the option object
  // is kept alongside so the Autocomplete can render its label/type without
  // a second lookup, mirroring selectedWasteStream above).
  const [selectedArea, setSelectedArea] = useState<AreaOption | null>(null);
  const [newAreaDialogOpen, setNewAreaDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  // The live validator flags the empty container type the moment a facility is
  // picked; suppress that field-level error text until the field is touched or
  // a save is attempted, so an untouched form doesn't open pre-shouting. The
  // issue counter and disabled Save still reflect the issue immediately.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const defaultValues = useWasteContainerFormDefaults(container, facilityId);

  const validateInput = useWasteContainerInputValidation(
    mode,
    container?.accumulationArea?.id ?? null,
    container?.id ?? null
  );
  const form = useValidatingForm<WasteContainerFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    setValue,
    watch,
    issues,
    formState: { isDirty, dirtyFields },
  } = form;

  const watchedFacilityId = watch("facilityId");
  const watchedIsPermanent = watch("isPermanent");
  const watchedSqgOver200Miles = watch("sqgOver200Miles");

  const { facilityContext, primaryEpaId, generatorStatus } =
    useFacilityContainerContext(watchedFacilityId);

  // Placement is create-only, and a tank must sit in a central area. A
  // container already in a satellite area can never be made permanent from
  // this form, so lock the checkbox instead of letting the user walk into an
  // unresolvable critical placement issue that leaves Save disabled.
  const tankPlacementLocked =
    mode === "edit" &&
    container?.accumulationArea != null &&
    container.accumulationArea.type !== WasteAccumulationAreaType.Central;

  const visibleAreaOptions = useVisibleAreaOptions({
    mode,
    facilityId: watchedFacilityId,
    isPermanent: watchedIsPermanent,
    selectedArea,
  });

  const handleAreaCreated = useCallback(
    (area: CreatedAccumulationArea) => {
      setSelectedArea(area);
      setValue("accumulationAreaId", area.id, { shouldDirty: true, shouldValidate: true });
      setNewAreaDialogOpen(false);
    },
    [setValue]
  );

  const prefillCapacityForType = usePrefillCapacityForType(getValues, setValue);

  const { onSubmit, isSaving } = useSaveWasteContainer({
    mode,
    container,
    onClose,
    onCreated,
    validateInput,
    getValues,
    dirtyFields,
    setSubmitAttempted,
  });

  return {
    mode,
    form,
    control,
    setValue,
    issues,
    isDirty,
    onSubmit,
    isSaving,
    selectedWasteStream,
    setSelectedWasteStream,
    selectedArea,
    setSelectedArea,
    newAreaDialogOpen,
    setNewAreaDialogOpen,
    moveDialogOpen,
    setMoveDialogOpen,
    submitAttempted,
    watchedFacilityId,
    watchedIsPermanent,
    watchedSqgOver200Miles,
    facilityContext,
    primaryEpaId,
    generatorStatus,
    tankPlacementLocked,
    visibleAreaOptions,
    handleAreaCreated,
    prefillCapacityForType,
  };
}

/**
 * The dialogs this one can open over itself: creating an area inline while
 * placing a new container, and moving an existing one. Mutually exclusive by
 * mode, so they sit together rather than beside the form they interrupt.
 */
function ContainerSideDialogs({
  state,
  container,
}: {
  state: ReturnType<typeof useWasteContainerDialogState>;
  container?: WasteContainerDetail;
}) {
  const {
    mode,
    newAreaDialogOpen,
    setNewAreaDialogOpen,
    watchedFacilityId,
    watchedIsPermanent,
    handleAreaCreated,
    moveDialogOpen,
    setMoveDialogOpen,
  } = state;

  return (
    <>
      {mode === "create" && newAreaDialogOpen && watchedFacilityId && (
        <AccumulationAreaDialog
          open
          facilityId={watchedFacilityId}
          onClose={() => setNewAreaDialogOpen(false)}
          onCreated={handleAreaCreated}
          defaultType={watchedIsPermanent ? WasteAccumulationAreaType.Central : undefined}
        />
      )}
      {mode === "edit" && container && (
        <MoveContainerDialog
          container={container}
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
        />
      )}
    </>
  );
}

export function WasteContainerDialog(props: WasteContainerDialogProps) {
  const { onClose, facilityId, container } = props;
  const state = useWasteContainerDialogState(props);
  const {
    mode,
    form,
    control,
    setValue,
    issues,
    isDirty,
    onSubmit,
    isSaving,
    selectedWasteStream,
    setSelectedWasteStream,
    selectedArea,
    setSelectedArea,
    setNewAreaDialogOpen,
    setMoveDialogOpen,
    submitAttempted,
    watchedFacilityId,
    watchedIsPermanent,
    watchedSqgOver200Miles,
    facilityContext,
    primaryEpaId,
    generatorStatus,
    tankPlacementLocked,
    visibleAreaOptions,
    prefillCapacityForType,
  } = state;

  // Only when creating: editing can't move the dates or the initial amount the
  // projection is built from, so the rail would be a static restatement of the
  // container's own drawer.
  const showPreview = mode === "create";

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={showPreview ? "lg" : "sm"}
        title={mode === "create" ? "Add container" : "Edit container"}
        subheader={
          showPreview
            ? "Track from a few dates, or log every fill event. The accumulation clock and fill charts update live as you go."
            : "Track from a few dates, or log every fill event."
        }
        primaryActionButtonProps={{
          loading: isSaving,
          onClick: onSubmit,
          disabled: hasCriticalIssues(issues) || !isDirty,
        }}
        additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      >
        <FormProvider {...form}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems="flex-start"
            component="form"
            sx={{ pt: 1 }}
          >
            <ContainerFormFields
              mode={mode}
              facilityId={facilityId}
              container={container}
              control={control}
              setValue={setValue}
              selectedWasteStream={selectedWasteStream}
              setSelectedWasteStream={setSelectedWasteStream}
              selectedArea={selectedArea}
              setSelectedArea={setSelectedArea}
              watchedFacilityId={watchedFacilityId}
              watchedIsPermanent={watchedIsPermanent}
              watchedSqgOver200Miles={watchedSqgOver200Miles}
              facilityContext={facilityContext}
              primaryEpaId={primaryEpaId}
              tankPlacementLocked={tankPlacementLocked}
              visibleAreaOptions={visibleAreaOptions}
              setNewAreaDialogOpen={setNewAreaDialogOpen}
              setMoveDialogOpen={setMoveDialogOpen}
              prefillCapacityForType={prefillCapacityForType}
              generatorStatus={generatorStatus}
              submitAttempted={submitAttempted}
            />
            {showPreview && <ContainerPreviewRail form={form} generatorStatus={generatorStatus} />}
          </Stack>
        </FormProvider>
      </Dialog>
      <ContainerSideDialogs state={state} container={container} />
    </>
  );
}
